import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { getPointsAfterWidthOrHeightChange } from '@src/slide/infrastructure/module/independent/graphic/resize';
import { IFrame, MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { isShapeAutoFit } from '@src/slide/domain/ui-model/element-modeles/property/text/text-adjust/utils';
import TextAdjustManager from '@src/slide/domain/ui-model/element-modeles/property/text/text-adjust';
import { IElementFrame } from '@src/slide/domain/ui-model/element-modeles/property/text/text-adjust/text-adjust.interface';
import { toJS } from 'mobx';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IConnectorModel, ILeftAndTop } from '@src/slide/domain/ui-model/element-modeles/Interface/connector-model.interface';
import { getGroupChildRelativeProps, getOffsetWithRelative } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/group-condition';
import { transaction } from '@src/slide/domain/slide-app';
import { ERectProperty, IBatchUpdateFrameProperty, IMultiValueProps } from './batch-update.interface';
import { IMultiFrameProps } from '../multiply/index.interface';
import { enumerateGroup } from '../../group-operation/enumerate';
import { GroupChildrenFrameHanler } from '../group/group-children-frame';

export enum EWidthOrHeight {
    WIDTH = 'width',
    HEIGHT = 'height',
}
const DEFAULT_SCALE = 1; // 默认缩放大小
const DEFAULT_VAULE = 0;

interface IUpdateModelInfoProps {
    updateModel: IElementBaseModel;
    multiFrameMap: IMultiModelFrameMap;
    updateModels: IElementBaseModel[];
    updatePropsArr: any[];
    removeModels: IElementBaseModel[];
    removePropsArr: any[];
    type?: ERectProperty;
}
interface IAdjustGroupProps {
    updateModels: IElementBaseModel[],
    removeModels: IElementBaseModel[],
    removePropsArr: any[],
    updatePropsArr: any[],
    multiFrameMap?: IMultiModelFrameMap,
}
interface IRelatedInfoProps {
    updateModel: IElementBaseModel,
    uiModels: IElementBaseModel[],
    updatePropsArr: any[],
    toUpdateModelFrameMap?: IMultiModelFrameMap,
    isOnlyMove?: boolean,
}

type IMultiModelFrameMap = Map<IElementBaseModel['key'], IFrame | null>; // key为model.key, value为model待更新的frame

export default class BatchUpdateFrame implements IBatchUpdateFrameProperty {
    private selectionService?: ISelectionService;
    private updateModelArr: IElementBaseModel[] = []; // 待更新的元素数组
    private mode: string;
    private applyShapeAutoFit?: boolean; // 是否要支持文本框根据文字调整形状

    public constructor(selectionService?: ISelectionService) {
        this.selectionService = selectionService;
    }

    public batchUpdateWidth(props: IMultiValueProps): void {
        if (!this.needUpdate(props.value, props.updateModelArr)) return;
        this.updateMultiWidthOrHeight(props, EWidthOrHeight.WIDTH);
    }

    public batchUpdateHeight(props: IMultiValueProps): void {
        if (!this.needUpdate(props.value, props.updateModelArr)) return;
        this.updateMultiWidthOrHeight(props, EWidthOrHeight.HEIGHT);
    }

    public batchUpdateLeft(value: number[], updateModelArr: IElementBaseModel[], isOnSlide?: boolean): void {
        if (!this.needUpdate(value, updateModelArr)) return;
        this.updateLeftOrTop(value, updateModelArr, ERectProperty.LEFT, isOnSlide);
    }

    public batchUpdateTop(value: number[], updateModelArr: IElementBaseModel[], isOnSlide?: boolean): void {
        if (!this.needUpdate(value, updateModelArr)) return;
        this.updateLeftOrTop(value, updateModelArr, ERectProperty.TOP, isOnSlide);
    }

    /** 旋转至指定角度 */
    public batchUpdateRotateToAngle(
        value: number | number[], updateModelArr: IElementBaseModel[],
        isOnSlide?: boolean,
    ) {
        this.batchUpdateRotate(value, updateModelArr, isOnSlide, false);
    }

    /** 在现有旋转基础上，再旋转 */
    public batchUpdateRotateWithAngle(
        value: number | number[], updateModelArr: IElementBaseModel[],
        isOnSlide?: boolean,
    ) {
        this.batchUpdateRotate(value, updateModelArr, isOnSlide, true);
    }

    public batchUpdateFrame(props: IMultiFrameProps): void {
        if (!this.needUpdate(props.frame, props.updateModelArr)) return;
        this.updateData(props.mode, props.applyShapeAutoFit);
        this.updateModelArr = [...props.updateModelArr];
        const multiFrameMap = new Map();
        this.updateModelArr.forEach((model, index) => {
            multiFrameMap.set(model.key, { ...model.getFrame().getFrameInfo(), ...props.frame[index] });
        });
        this.batchUpdateModelFrame(multiFrameMap);
    }

    public batchUpdateFrameOnSlide(props: IMultiFrameProps) {
        if (!this.needUpdate(props.frame, props.updateModelArr)) return;
        this.updateModelArr = [...props.updateModelArr];
        this.updateData(props.mode, props.applyShapeAutoFit);
        const { frame } = props;
        const multiFrameMap = new Map();
        this.updateModelArr.forEach((model: IElementBaseModel, index) => {
            const updatedFrameOnSlide = Array.isArray(frame) ? frame[index] : frame;
            let updatedFrame = { ...model.getFrame().frameOnSlide(), ...updatedFrameOnSlide };
            if (model.parent?.type === UIModelType.GROUP) {
                updatedFrame = this.convertModelFrameOnSlideToSelf(model, updatedFrame);
            }
            multiFrameMap.set(model.key, updatedFrame);
        });
        this.batchUpdateModelFrame(multiFrameMap);
    }


    private batchUpdateRotate(
        value: number | number[], updateModelArr: IElementBaseModel[],
        isOnSlide?: boolean, byCurrent?: boolean,
    ): void {
        const rotateMap = new Map();
        const updateModels: IElementBaseModel[] = [];
        const updatePropsArr: any[] = [];
        const removeModels: IElementBaseModel[] = [];
        const removePropsArr: any[] = [];
        this.updateModelArr = [...updateModelArr];
        this.updateModelArr.forEach((model, index) => {
            const angle = Array.isArray(value) ? value[index] : value;
            if (model.type === UIModelType.TABLE) return;
            const finalRotate = byCurrent
                ? this.getFinalRotateAngle(model, angle) : this.getRotateAngle(model, angle, isOnSlide);
            rotateMap.set(model.key, { rotate: finalRotate });
            updatePropsArr.push({ style: { ...model.getFrame().getFrameInfo(), rotate: finalRotate } });
            updateModels.push(model);
            this.handleRelatedInfo(model, updateModels, updatePropsArr, rotateMap);
            this.updateRelatedInfoForConnector(model, true);
            this.handleConnectorRemoveInfo(model, removeModels, removePropsArr);
        });
        this.commitChange(removeModels, updateModels, removePropsArr, updatePropsArr);
    }

    private convertModelFrameOnSlideToSelf(model, frameOfSlide: IFrame): IFrame {
        return new GroupChildrenFrameHanler(model).convertFrameOnSlideToSelf(frameOfSlide);
    }

    /**
     * 处理宽高变化
     */
    private updateMultiWidthOrHeight = (props: IMultiValueProps, type: EWidthOrHeight) => {
        this.updateData(props.mode, props.applyShapeAutoFit);
        this.updateModelArr = [...props.updateModelArr];
        const multiFrameMap = new Map();
        this.updateModelArr.forEach((model, index) => {
            const finalFrame = this.getFrameWhenWidthOrHeightchange(
                props.value[index], type,
                model, props.isIgnoreLock,
            );
            multiFrameMap.set(model.key, finalFrame);
        });
        this.batchUpdateModelFrame(multiFrameMap);
    };

    /**
     * 批量更新选中元素的大小
     */
    private batchUpdateModelFrame(multiFrameMap: IMultiModelFrameMap, type?: ERectProperty) {
        transaction(() => {
            const updateModels: IElementBaseModel[] = [];
            const updatePropsArr: any[] = [];
            const removeModels: IElementBaseModel[] = [];
            const removePropsArr: any[] = [];

            const connectorModels: IConnectorModel[] = [];
            this.updateModelArr.forEach((updateModel: IElementBaseModel) => {
                if (this.isConnectedConnector(updateModel as IConnectorModel)) {
                    connectorModels.push(updateModel as IConnectorModel);
                } else {
                    this.modifyUpdateInfo({
                        updateModel, multiFrameMap, type,
                        updateModels, updatePropsArr, removeModels, removePropsArr,
                    });
                }
            });
            // 待其他元素的数据处理完后，最后一端连接元素的且连接线也需要更新的情况
            connectorModels.forEach((updateModel: IConnectorModel) => {
                this.handleConnectedConnector({ updateModel, uiModels: updateModels,
                    updatePropsArr, toUpdateModelFrameMap: multiFrameMap });
            });
            if (this.checkIsGroup()) {
                this.persistSizeForGroup({
                    multiFrameMap, updateModels, updatePropsArr,
                    removeModels, removePropsArr,
                });
            } else {
                this.commitChange(removeModels, updateModels, removePropsArr, updatePropsArr);
            }
        });
    }

    /**
     * 修正待更新的元素信息，包括：
     * 1.连接线：元素如果与连接线相连，需更新连接线信息
     * 2.表格：表格有特定的更新frame的逻辑
     * 3.文本框：包括是否根据文字调整形状大小
     */
    private modifyUpdateInfo(props: IUpdateModelInfoProps) {
        const {
            updateModel, multiFrameMap, type,
            updateModels, updatePropsArr, removeModels, removePropsArr,
        } = props;
        const finalFrame = multiFrameMap.get(updateModel.key);
        if (!finalFrame) return;

        const isOnlyMove = type && ['left', 'top'].includes(type);

        this.updateRelatedInfoForConnector(updateModel, isOnlyMove);
        this.handleConnectorRemoveInfo(updateModel, removeModels, removePropsArr);

        updateModels.push(updateModel);
        const info = this.getNextStyle(updateModel, finalFrame, isOnlyMove);
        updatePropsArr.push(info);

        multiFrameMap?.set(updateModel.key, info.style);

        this.handleRelatedInfo(updateModel, updateModels, updatePropsArr, multiFrameMap, isOnlyMove);
    }

    /**
     * 获取设置宽度或者高度时的offsetFrame
     */
    private getFrameWhenWidthOrHeightchange = (
        value: number, type: EWidthOrHeight, model: IElementBaseModel,
        isIgnoreLock?: boolean,
    ): IFrame | null => {
        if ((model as IShapeModel).isTextAttachment?.()) return null;
        const frame = { ...model.getFrame().getFrameInfo(), [type]: value };
        this.handleLockAspect(type, frame, model, isIgnoreLock);
        this.handleLeftTopWhenHeightOrWidthChange(model, frame);
        return frame;
    };

    /**
     * 处理锁定纵横比元素的宽高变化
     */
    private handleLockAspect = (
        type: EWidthOrHeight, frame: IFrame, model: IElementBaseModel,
        isIgnoreLock?: boolean,
    ) => {
        if (!model.props?.locks?.noChangeAspect || isIgnoreLock) return;
        let scale: number;
        // 锁定纵横比的元素如果宽/高置为0，纵横比置为1。对齐office逻辑
        if (model.props.style.width === 0 || model.props.style.height === 0) {
            scale = DEFAULT_SCALE;
        } else {
            scale = model.props.style.width / model.props.style.height;
        }
        if (type === EWidthOrHeight.WIDTH) {
            frame.height = frame[type] / scale;
        } else {
            frame.width = frame[type] * scale;
        }
    };

    /**
     * 当设置width/height时，相应的修改元素的left/top
     */
    private handleLeftTopWhenHeightOrWidthChange = (model: IElementBaseModel, sizeProps: IFrame) => {
        const { rotate } = model.props.style;
        // 当且仅当元素水平翻转后，修改裁剪宽度时mode为LEFT，否则为RIGHT
        const isLeftChange = this.mode === MODES.LEFT;
        // 当且仅当元素垂直翻转后，修改裁剪高度时mode为TOP，否则为BOTTOM
        const isTopChange = this.mode === MODES.TOP;
        const widthOffset = sizeProps.width - model.props.style.width;
        const heightOffset = sizeProps.height - model.props.style.height;
        if (!rotate) {
            if (isLeftChange) {
                sizeProps.left -= widthOffset;
            }
            if (isTopChange) {
                sizeProps.top -= heightOffset;
            }
            return;
        }
        const [firstPoint] = getPointsAfterWidthOrHeightChange({
            originalFrame: model.props.style, rotate, widthOffset, heightOffset,
            isLeftChange: Boolean(isLeftChange), isTopChange: Boolean(isTopChange),
        });
        sizeProps.left = firstPoint.x;
        sizeProps.top = firstPoint.y;
    };

    private persistSizeForGroup = (props: IAdjustGroupProps) => {
        const { updatePropsArr, updateModels, multiFrameMap, removeModels, removePropsArr } = props;
        const modelsProps: IFrame[] = [];
        this.updateModelArr.forEach((model) => {
            modelsProps.push(multiFrameMap?.get(model.key) as IFrame);
        });
        const rootGroupModel = this.getRootGroupModel();
        if (rootGroupModel) {
            // 先调整group相关数据， element如果发生旋转或者翻转后，会依赖于group上的变更追加宽高变更的changeProArr
            rootGroupModel.getFrame()
                .getAdjustedElementsAndProps(this.updateModelArr, modelsProps)
                .forEach((item) => {
                    updatePropsArr.push(item.props);
                    updateModels.push(item.uiModel);
                });
        }
        this.commitChange(removeModels, updateModels, removePropsArr, updatePropsArr);
        if (this.updateModelArr?.length > 0) {
            rootGroupModel?.getFrame().clearTempInfosWithElement(this.updateModelArr[0]);
        }
    };

    private frameInfoAfterAdjust(model, finalFrame: IFrame) {
        const frame = this.getFrameInfoOfModel(model, finalFrame);
        const { style } = TextAdjustManager.getInstance().adjustShape(model, frame, this.mode);
        return style;
    }

    private updateData = (mode?: MODES, applyShapeAutoFit?: boolean) => {
        this.mode = mode ?? MODES.BOTTOM_RIGHT;
        this.applyShapeAutoFit = applyShapeAutoFit;
    };

    private isNeedToAdjustText = (model): boolean | undefined => TextAdjustManager.getInstance().enableAdjustText()
        && isShapeAutoFit(model)
        && this.applyShapeAutoFit;

    private getNextStyle = (updateModel: IElementBaseModel, finalFrame: IFrame, isOnlyMove?: boolean) => {
        let info;
        if (updateModel?.type === UIModelType.TABLE && !isOnlyMove) {
            info = (updateModel as ITableModel).resizeTable(finalFrame, this.mode);
        } else {
            if (this.isNeedToAdjustText(updateModel)) {
                // 形状设置了跟随文本大小调整属性，需要对frame信息进行调整
                info = this.frameInfoAfterAdjust(updateModel, finalFrame);
            } else {
                info = finalFrame;
            }
            info = { style: info };
        }
        return info;
    };

    /**
    * 返回model的operationFrame信息
    */
    private getFrameInfoOfModel = (model: IElementBaseModel, finalFrame?: IFrame): IElementFrame => {
        const frameInfo = model.getFrame?.().frameOnSlide();
        const getItem = (key: string) => (typeof finalFrame?.[key] !== 'undefined' ? finalFrame[key] : frameInfo?.[key]);
        return {
            style: {
                left: getItem('left'),
                top: getItem('top'),
                width: getItem('width'),
                height: getItem('height'),
                flipH: getItem('flipH'),
                flipV: getItem('flipV'),
                rotate: model.props?.style?.rotate ?? DEFAULT_VAULE,
            },
        };
    };

    /** left/top change start */
    /**
     * 更新left/top
     * @param value 更新的left/top
     * @param type left还是top
     * @param isOnSlide 是否相对slide
     */
    private updateLeftOrTop(
        value: number[], updateModelArr: IElementBaseModel[],
        type: ERectProperty, isOnSlide?: boolean,
    ) {
        const valueMap = new Map();
        this.updateModelArr = [...updateModelArr];
        this.updateModelArr.forEach((model: IElementBaseModel, index) => {
            const { left, top, width, height } = model.props.style;
            const newFrame = {
                left, top, width, height,
                [type]: this.getFinalLeftOrTop(model, value[index], type, isOnSlide),
            };
            if (model.parent?.type === UIModelType.GROUP) {
                // 当元素为组合子元素时，需要对left/top进行修正
                const { diffLeft, diffTop } = this.getDiffLeftTop(model, value[index], type, isOnSlide);
                const offset = getOffsetWithRelative(getGroupChildRelativeProps(model), diffLeft, diffTop);
                newFrame.left = left + offset.offsetX;
                newFrame.top = top + offset.offsetY;
            }
            valueMap.set(model.key, newFrame);
        });
        this.batchUpdateModelFrame(valueMap, type);
    }

    /**
     * 获取最终的更新的值，如果是组合子元素且value相对slide，则单独处理
     * @param model 选中的model
     * @param value 待更新的值
     * @param type left还是top
     * @param isOnSlide 是否相对slide
     * @returns
     */
    private getFinalLeftOrTop = (
        model: IElementBaseModel, value: number,
        type: ERectProperty, isOnSlide?: boolean,
    ): number => {
        if (model?.parent?.type === UIModelType.GROUP && isOnSlide) {
            enumerateGroup(model, (groupModel) => {
                value -= groupModel.props.style[type];
            });
        }
        return value;
    };

    /**
     * 获取left/top改变的差值
     */
    private getDiffLeftTop = (
        model: IElementBaseModel, value: number, type: ERectProperty,
        isOnSlide?: boolean,
    ): { diffLeft: number, diffTop: number } => {
        let diffLeft = DEFAULT_VAULE;
        let diffTop = DEFAULT_VAULE;
        const frameOnSlide = model.getFrame?.().frameOnSlide();
        const { left, top } = model.props.style;
        if (isOnSlide) {
            if (type === ERectProperty.LEFT) {
                diffLeft = value - frameOnSlide.left;
            } else {
                diffTop = value - frameOnSlide.top;
            }
        } else {
            if (type === ERectProperty.LEFT) {
                diffLeft = value - left;
            } else {
                diffTop = value - top;
            }
        }
        return { diffLeft, diffTop };
    };
    /** left/top change end */

    /**
     * 提交待修改或待删除的数据
     */
    private commitChange(removeModels: IElementBaseModel[], uiModels, removePropsArr: any[], updatePropsArr) {
        if (removeModels.length > 0) {
            uiModels.forEach(() => removePropsArr.unshift(undefined)); // 补齐前面的空对象
            removeModels.forEach(() => updatePropsArr.push(null));
            this.selectionService?.currentSlide?.updateAndRemovePropsForElements(
                updatePropsArr,
                removePropsArr,
                uiModels.concat(removeModels),
            );
        } else {
            this.selectionService?.currentSlide?.updateElementsProps(updatePropsArr, uiModels);
        }
    }

    /** 处理连接线相关逻辑 start */
    /**
      * 单独处理连接线的frame变更
      */
    private handleConnectedConnector(props: IRelatedInfoProps): void {
        const { uiModels, updatePropsArr, toUpdateModelFrameMap, isOnlyMove } = props;
        const uiModel = props.updateModel as IConnectorModel;
        let style;
        if (toUpdateModelFrameMap) {
            const onlyMoveStyleProps = this.getOffsetFrameForConnectorOnlyMove(
                uiModel, isOnlyMove,
                toUpdateModelFrameMap,
            );
            if (onlyMoveStyleProps) {
                style = {
                    ...uiModel.getUpdateStylePropsForOnlyMove(onlyMoveStyleProps),
                    bgLine: uiModel.props.style.bgLine,
                    controlValues: toJS(uiModel.props.style.controlValues),
                };
            } else {
                const { frame, bgLine, controlValues } = uiModel.getUpdateStyleProps(toUpdateModelFrameMap);
                style = { ...frame, bgLine, controlValues };
            }
        } else {
            style = {
                ...uiModel.operationFrame,
                bgLine: uiModel.props.style.bgLine,
                controlValues: toJS(uiModel.props.style.controlValues),
            };
        }
        uiModels.push(uiModel);
        updatePropsArr.push({ style });
    }

    /**
     * 处理元素连接线逻辑，且连接线未被选中。
     */
    private handleRelatedInfo = (
        updateModel: IElementBaseModel, uiModels, updatePropsArr,
        toUpdateModelFrameMap?: IMultiModelFrameMap, isOnlyMove?: boolean,
    ): void => {
        updateModel?.relatedUIModelRefs?.forEach((relatedUIModel) => {
            // 当连接线也需更新时，无需在此处处理
            if (this.updateModelArr.includes(relatedUIModel)) return;
            // 当形状有连接线时，需要处理连接线
            if (relatedUIModel.type === UIModelType.CONNECTOR) {
                this.handleConnectedConnector({
                    updateModel: relatedUIModel, uiModels,
                    updatePropsArr, toUpdateModelFrameMap, isOnlyMove,
                });
            }
        });
    };

    /**
     * 当移动/旋转连接线时，更新连接线端点信息。
    */
    private updateRelatedInfoForConnector(updateModel: IElementBaseModel, isOnlyMove?: boolean) {
        if (updateModel.type === UIModelType.CONNECTOR) {
            // 移动: 仅移动连接线时断开连接的图形
            // 旋转：不管选中几个形状，只要连接线进行旋转都断开连接的图形
            (updateModel as IConnectorModel).resetStartAndEndForMove(isOnlyMove);
        }
    }

    /**
     * 处理连接线，获取要删除的关联图形信息
     */
    private handleConnectorRemoveInfo = (
        updateModel: IElementBaseModel,
        removeModels: IElementBaseModel[], removePropsArr: any[],
    ): void => {
        if (updateModel.type === UIModelType.CONNECTOR) {
            const toRemoveProps = (updateModel as IConnectorModel).toRemoveConnectorInfoProps();
            if (toRemoveProps) {
                removeModels.push(updateModel);
                removePropsArr.push(toRemoveProps);
            }
        }
    };

    /**
     * 判断连接线是否仅移动，且获取相应的offsetFrame
     */
    private getOffsetFrameForConnectorOnlyMove(
        model: IConnectorModel, isOnlyMove?: boolean,
        toUpdateModelFrameMap?: IMultiModelFrameMap,
    ): ILeftAndTop | undefined {
        if (!isOnlyMove) return;
        let nextframe;
        let connectedModel;
        // 如果连接线两端均被选中 或 连接线首端连接元素
        if ((model.startUIModel?.selected && model.endUIModel?.selected)
            || (model.startUIModel && !model.endUIModel)) {
            nextframe = toUpdateModelFrameMap?.get(model.startUIModel.key);
            connectedModel = model.startUIModel;
        }
        // 连接线末端连接元素
        if (!model.startUIModel && model.endUIModel) {
            nextframe = toUpdateModelFrameMap?.get(model.endUIModel.key);
            connectedModel = model.endUIModel;
        }
        if (nextframe) {
            return {
                left: nextframe.left - connectedModel.props.style.left,
                top: nextframe.top - connectedModel.props.style.top,
            };
        }
    }
    /** 处理连接线相关逻辑 end */

    private checkIsGroup() {
        return this.updateModelArr[0].parent?.type === UIModelType.GROUP;
    }

    private getRootGroupModel() {
        return this.updateModelArr[0].rootGroupModel;
    }

    private getRotateAngle = (model: IElementBaseModel, rotate: number, isRotateOnSlide?: boolean): number => {
        if (model?.parent?.type === UIModelType.GROUP && isRotateOnSlide) {
            const realFrameOnSlideRotate = model.getFrame?.().frameOnSlide().rotate ?? DEFAULT_VAULE;
            const relativeRotate = (realFrameOnSlideRotate + 360 - (model.props.style.rotate ?? DEFAULT_VAULE)) % 360;
            return (rotate - relativeRotate + 360) % 360;
        }
        return rotate;
    };

    private getFinalRotateAngle = (model: IElementBaseModel, rotate: number): number => {
        const currentRotate = (model.props.style.rotate ?? DEFAULT_VAULE) % 360;
        return (rotate + currentRotate + 360) % 360;
    };

    /**
     * 连接线的一端连接元素，且不是两端都连接
     */
    private isConnectedConnector = (model: IConnectorModel): boolean => model.type === UIModelType.CONNECTOR
        && !(model.startUIModel && model.endUIModel)
        && !!((model.startUIModel && this.updateModelArr.includes(model.startUIModel))
            || (model.startUIModel && this.updateModelArr.includes(model.startUIModel)));

    private needUpdate<T>(value: T[], updateModelArr: IElementBaseModel[]) {
        return value.length > 0 && updateModelArr.length > 0;
    }
}
